/* src/components/block/broadcastingChannelManagment/SourceSelector.tsx */

import React, { useContext } from 'react';
import styles from './SourceSelector.module.scss';

import { Trigger } from '../../widget/actuators/Trigger';
import { AnimatedWaitingIcon } from '../../widget/animations/animatedWaitingIcon';
import { StreamSource } from '../../../dataTypes/broadcastingManagment/types'

/**
 * Source en cours de bascule (null = aucune). Posée par la fiche de la chaîne le temps que le
 * serveur confirme : choisir « Source externe RTMP » peut prendre une douzaine de secondes, le
 * temps qu'OBS soit connecté. Un contexte plutôt qu'une propriété : ce sélecteur est monté à
 * deux endroits, dont un en profondeur sous la régie.
 */
export const SourceSwitchingContext = React.createContext<StreamSource | null>(null);

export interface SourceOption {
  value: StreamSource;
  label: string;
}

export const SOURCE_OPTIONS: SourceOption[] = [
  { value: 'video',  label: 'Mire / playlist'     },
  { value: 'rtmp',   label: 'Source externe RTMP' },
  { value: 'webrtc', label: 'Régie'          },
];

interface SourceSelectorProps {
  streamSource:           StreamSource;
  defaultSource:          StreamSource;
  onStreamSourceChange:   (value: StreamSource) => void;
  onDefaultStreamSourceChange: (value: StreamSource) => void; 
  unavailableSources?:    StreamSource[];
  /** Chaîne arrêtée : la source reste sur Mire / playlist, aucun changement possible. */
  locked?:                boolean;

  activeBgColor?:         string;
  activeFgColor?:         string;
  inactiveBgColor?:       string;
  inactiveFgColor?:       string;
  unavailableBgColor?:    string;
  unavailableFgColor?:    string;

  radioOnColor?:          string; // ← couleur radio coché
  radioOffColor?:         string; // ← couleur radio décoché
  radioGroupName?: string;

  fontSize?: string;
}

export default function SourceSelector({
  streamSource,
  defaultSource,
  onStreamSourceChange,
  onDefaultStreamSourceChange,
  unavailableSources    = [],
  locked                = false,

  activeBgColor         = '#25C400',
  activeFgColor         = '#000000',
  inactiveBgColor       = 'var(--color-2)',
  inactiveFgColor       = 'var(--color-0)',
  unavailableBgColor    = 'var(--color-Button-BG-disable)',
  unavailableFgColor    = 'var(--color-6)',

  radioOnColor          = '#25C400',
  radioOffColor         = '#888888',
  radioGroupName        = 'defaultSource',

  fontSize,
  
}: SourceSelectorProps) {
  const switching = useContext(SourceSwitchingContext);
  return (
    <div
      className={styles.wrapper}
      style={{
        '--radio-on-color':  radioOnColor,
        '--radio-off-color': radioOffColor,
		fontSize:       fontSize || '16px',
      } as React.CSSProperties}
    >
      {/* Column headers */}
      <div className={styles.row}>
        <div className={styles.colHeaderSource}>Source active</div>
        <div className={styles.colHeaderDefault}>Source par défaut</div>
      </div>

      {/* Rows */}
      {SOURCE_OPTIONS.map((opt) => {
        const isActive      = locked ? opt.value === 'video' : streamSource === opt.value;
        const isDefault     = defaultSource === opt.value;
        const isUnavailable = unavailableSources.includes(opt.value);

        const bgColor = isUnavailable
          ? unavailableBgColor
          : isActive
            ? activeBgColor
            : inactiveBgColor;

        const fgColor = isUnavailable
          ? unavailableFgColor
          : isActive
            ? activeFgColor
            : inactiveFgColor;

        return (
          <div key={opt.value} className={styles.row}>

            <div className={locked ? `${styles.triggerCell} ${styles.lockedCell}` : styles.triggerCell} title={locked ? "Source verrouillée : la chaîne est arrêtée, elle démarre sur Mire / playlist" : undefined}>
            <Trigger
              name={opt.label}
              onClick={() => { if (!switching && !locked) onStreamSourceChange(opt.value); }}
              isActive={isActive}
              unavailable={isUnavailable}
              variant="default"
              width="100%"
              padding="2px 5px"
              margin="0"
              buttonBackgroundSelectedColor={bgColor}
              buttonForegroundSelectedColor={fgColor}
              buttonBackgroundColor={bgColor}
              buttonForegroundColor={fgColor}
              buttonBackgroundHoverColor={bgColor}
              buttonForegroundHoverColor={fgColor}	  
              iconColor={fgColor} 
   		      fontSize={fontSize} />
            {switching === opt.value && (
              <span className={styles.connecting} role="status" aria-label="Connexion en cours" title="Connexion en cours…">
                <AnimatedWaitingIcon width={22} height={22} />
              </span>
            )}
            </div>

<div className={styles.radioCell}>
  {/* La régie (webrtc) ne peut pas être source par défaut */}
  {opt.value !== 'webrtc' && (
    <input
      type="radio"
      name={radioGroupName} 
      value={opt.value}
      checked={isDefault}
      onChange={() => onDefaultStreamSourceChange(opt.value)}
      className={styles.radio}
      aria-label={`Par défaut : ${opt.label}`}
    />
  )}
</div>

          </div>
        );
      })}
    </div>
  );
}